import { useState, useEffect, forwardRef } from 'react';
import { Card, Table, Badge, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { FaComments, FaEnvelope, FaWhatsapp, FaSms, FaPhone, FaSearch, FaEye, FaCalendarAlt, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { timeAgo } from '../../utils/formatters';

const CHANNEL_ICONS = {
  EMAIL_SMTP: FaEnvelope,
  EMAIL_API: FaEnvelope,
  WHATSAPP_BUSINESS: FaWhatsapp,
  SMS: FaSms,
  VOICE: FaPhone,
};

const STATUS_COLORS = {
  OPEN: 'success',
  CLOSED: 'secondary',
};

const DateRangeInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <InputGroup size="sm" style={{ width: '220px', cursor: 'pointer' }} onClick={onClick} ref={ref}>
    <InputGroup.Text><FaCalendarAlt size={12} /></InputGroup.Text>
    <Form.Control
      readOnly
      size="sm"
      style={{ cursor: 'pointer', backgroundColor: 'white' }}
      value={value || ''}
      placeholder={placeholder}
    />
  </InputGroup>
));

function ConversationList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [filters, setFilters] = useState({ status: '', leadId: '', search: '', channelType: '', dateFrom: '', dateTo: '' });
  const [dateRange, setDateRange] = useState([null, null]);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Search state
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchConversations();
  }, [pagination.page, filters.status, filters.search, filters.channelType, filters.dateFrom, filters.dateTo]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== filters.search) {
        setFilters((prev) => ({ ...prev, search: trimmed }));
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.channelType) params.append('channelType', filters.channelType);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await api.get(`/conversations?${params}`);
      setConversations(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.pagination?.total || 0,
        totalPages: response.data.meta?.pagination?.totalPages || 1,
      }));
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/conversations/${deleteTarget.id}`);
      toast.success('Conversation deleted');
      setDeleteTarget(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  const getChannelIcon = (channelType) => {
    const Icon = CHANNEL_ICONS[channelType] || FaComments;
    return <Icon />;
  };

  const truncateContent = (content, maxLength = 60) => {
    if (!content) return 'No message';
    // Strip HTML tags for preview
    const text = content.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading && conversations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>
            <FaComments className="me-2" />
            Conversations
          </h1>
          <p className="text-muted mb-0">View email and WhatsApp threads with your contacts</p>
        </div>
      </div>

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Form.Select
                size="sm"
                style={{ width: '130px' }}
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All Status</option>
                <option value="OPEN">Open</option>
                <option value="CLOSED">Closed</option>
              </Form.Select>
              <Form.Select
                size="sm"
                style={{ width: '140px' }}
                value={filters.channelType}
                onChange={(e) => {
                  setFilters({ ...filters, channelType: e.target.value });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All Channels</option>
                <option value="EMAIL_SMTP">Email</option>
                <option value="WHATSAPP_BUSINESS">WhatsApp</option>
              </Form.Select>
              <DatePicker
                selectsRange
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={(update) => {
                  setDateRange(update);
                  const [start, end] = update;
                  setFilters({
                    ...filters,
                    dateFrom: start ? start.toISOString().split('T')[0] : '',
                    dateTo: end ? end.toISOString().split('T')[0] : '',
                  });
                  if ((start && end) || (!start && !end)) {
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }
                }}
                isClearable
                placeholderText="Select date range"
                dateFormat="MMM d, yyyy"
                maxDate={new Date()}
                customInput={<DateRangeInput />}
              />
              <InputGroup size="sm" style={{ width: '220px' }}>
                <InputGroup.Text>
                  <FaSearch size={12} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search contact, company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>
              {(filters.status || filters.search || filters.channelType || filters.dateFrom || filters.dateTo) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFilters({ status: '', leadId: '', search: '', channelType: '', dateFrom: '', dateTo: '' });
                    setDateRange([null, null]);
                    setSearchInput('');
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  Clear All
                </Button>
              )}
            </div>
            <span className="text-muted small">{pagination.total} conversations</span>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {conversations.length === 0 ? (
            <div className="text-center py-5">
              <FaComments size={48} className="text-muted mb-3" />
              <h5>No conversations yet</h5>
              <p className="text-muted">
                Conversations will appear here when you message contacts or they reply.
              </p>
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}></th>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Last Message</th>
                  <th style={{ width: '120px' }}>Status</th>
                  <th style={{ width: '100px' }}>Date</th>
                  <th style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conv) => (
                  <tr
                    key={conv.id}
                    style={{ cursor: 'pointer', fontWeight: conv.hasUnread ? '600' : 'normal' }}
                    onClick={() => navigate(`/conversations/${conv.id}`)}
                  >
                    <td className="text-center text-muted">
                      {conv.hasUnread && (
                        <span className="d-inline-block rounded-circle bg-primary me-1" style={{ width: '8px', height: '8px' }}></span>
                      )}
                      {getChannelIcon(conv.channelType)}
                    </td>
                    <td>
                      <strong>{conv.contact?.name || 'Unknown'}</strong>
                      <div className="small text-muted" style={{ fontWeight: 'normal' }}>{conv.contact?.email || conv.contact?.phone}</div>
                    </td>
                    <td style={{ fontWeight: 'normal' }}>
                      {conv.lead?.companyName || '-'}
                    </td>
                    <td className="text-muted" style={{ fontWeight: 'normal' }}>
                      {conv.messages?.[0] && (
                        <>
                          {conv.messages[0].direction === 'INBOUND' ? (
                            <Badge bg="info" className="me-1">In</Badge>
                          ) : (
                            <Badge bg="secondary" className="me-1">Out</Badge>
                          )}
                          {truncateContent(conv.messages[0].content)}
                        </>
                      )}
                    </td>
                    <td>
                      <Badge bg={STATUS_COLORS[conv.status]}>{conv.status}</Badge>
                    </td>
                    <td className="text-muted small" style={{ fontWeight: 'normal' }}>
                      {timeAgo(conv.lastMessageAt)}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/conversations/${conv.id}`);
                          }}
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(conv);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {pagination.totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <span className="text-muted small">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </Card.Footer>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Conversation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this conversation with{' '}
          <strong>{deleteTarget?.contact?.name || deleteTarget?.contact?.phone || 'this contact'}</strong>?
          This will permanently remove all messages.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ConversationList;
